import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import Dialog from '../../../components/ui/Dialog';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import DisabledTabTooltip from '../../../components/ui/DisabledTabTooltip';
import NoDataState from '../../../components/ui/NoDataState';
import { useAuth } from '../../../hooks/useAuth';
import { useRBAC } from '../../../hooks/useRBAC';
import { useNominationToast } from '../../../hooks/useNominationToast';
import { getNominationErrorMessage } from '../../../utils/nominationErrorUtils';
import { nominationApprovalService, selfAppraisalService } from '../../../services';
import arrowBackIcon from '../../../assets/arrow_back.svg';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';
import { getOkrResultColumnLabel } from '../../../utils/cycleUtils';

// Interface for nomination approval details
interface NominationApprovalDetail {
  id: string;
  comments: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reportingManager?: string;
  nominationStatus: 'Approved' | 'Pending' | 'Rejected';

  // Add nomination lists
  superiors?: Employee[];
  dotted_superiors?: Employee[];
  subordinates?: Employee[];
  additional_subordinates?: Employee[];
  peers?: Employee[];
  stakeholders?: Employee[];
  nominationApprover?: string;
  employeeEmail?: string;
  
  // Add OKR data
  okrs?: OKRData[];
}

interface OKRData {
  id: number;
  objective: string;
  target: string;
  endYearResult: string;
  rating: number | null;
}

interface Employee {
  full_name: string;
  employee_id?: string;
  email?: string;
  designation?: string;
  department_name?: string;
  manager_name?: string;
}

interface NominationLists {
  superiors?: Employee[];
  dotted_superiors?: Employee[];
  subordinates?: Employee[];
  additional_subordinate?: Employee[];
  additional_subordinates?: Employee[];
  peers?: Employee[];
  stakeholders?: Employee[];
  nomination_manager?: Employee[];
}

interface ApiEmployee {
  employee_id?: string;
  full_name?: string;
  designation?: string;
  department_name?: string;
  manager_name?: string;
  email?: string;
}

interface ApiResponseData {
  nomination_id: number;
  status: string;
  comments: string;
  employee: ApiEmployee;
  nominations: NominationLists;
  okr_id?: number;
}

const getEmployeeName = (employee: { full_name?: string | null; email?: string | null }) =>
  employee.full_name?.trim() || employee.email || '';

const normalizeEmployees = (employees: Employee[] = []) =>
  employees.map((employee) => ({
    ...employee,
    full_name: getEmployeeName(employee),
  }));

const NominationApprovalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const { canApproveOKRs, canApproveNominations, appraisalCycle } = useRBAC();
  const hasActiveCycle = useActiveCycle();
  const resultColumnLabel = getOkrResultColumnLabel(appraisalCycle?.cycle_type);
  const { toastState, showError, closeToast } = useNominationToast();
  const [nomination, setNomination] = useState<NominationApprovalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRedirectDialog, setShowRedirectDialog] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(30);
  const [redirectDestination, setRedirectDestination] = useState<'team-review' | '360-feedback'>('team-review');
  const [rejectReason, setRejectReason] = useState('');
  
  // RBAC-based tab access and initial tab selection
  const hasOKRAccess = canApproveOKRs();
  const isStartOrMidCycle = appraisalCycle?.cycle_type === 0 || appraisalCycle?.cycle_type === 1;
  const hasNominationAccess = canApproveNominations() && !isStartOrMidCycle;
  
  // Determine initial active tab based on permissions
  const getInitialTab = (): 'okrs' | 'nominations' => {
    if (hasOKRAccess) return 'okrs';
    if (hasNominationAccess) return 'nominations';
    return 'okrs'; // fallback
  };
  
  const [activeTab, setActiveTab] = useState<'okrs' | 'nominations'>(getInitialTab());
  const [okrLoading, setOkrLoading] = useState(false);
  const [nominationLoading, setNominationLoading] = useState(false);
  const [okrId, setOkrId] = useState<number | null>(null);
  const [okrStatus, setOkrStatus] = useState<'Approved' | 'Pending' | 'Rejected' | null>(null);
  const [okrRejectedRemarks, setOkrRejectedRemarks] = useState<string | null>(null);
  const lastFetchedOkrIdRef = useRef<number | null>(null);
  const hasSetInitialTabFromUrl = useRef(false);

  const redirectAfterApproval = useCallback(() => {
    setShowRedirectDialog(false);
    if (redirectDestination === '360-feedback') {
      navigate('/360-feedback');
      return;
    }

    navigate(`/team-review/${id}?tab=okrs&emp_id=${encodeURIComponent(nomination?.employeeId || '')}`, {
      state: {
        nominationRelationshipId: id,
        employeeId: nomination?.employeeId,
        employeeName: nomination?.employeeName,
        designation: nomination?.designation,
        department: nomination?.department,
        employeeEmail: nomination?.employeeEmail,
        managerName: nomination?.managerName,
      },
    });
  }, [id, navigate, nomination, redirectDestination]);

  useEffect(() => {
    if (!showRedirectDialog) {
      return;
    }

    if (redirectCountdown === 0) {
      redirectAfterApproval();
      return;
    }

    const redirectTimer = window.setTimeout(() => {
      setRedirectCountdown((currentCountdown) => currentCountdown - 1);
    },1000);

    return () => window.clearTimeout(redirectTimer);
  }, [showRedirectDialog, redirectCountdown, redirectAfterApproval]);

  // Get okr_id and no_nomination flag from URL query params
  useEffect(() => {
    const okrIdFromUrl = searchParams.get('okr_id');
    const noNomination = searchParams.get('no_nomination');
    
    if (okrIdFromUrl) {
      setOkrId(parseInt(okrIdFromUrl, 10));
    }
    
    // Honor the requested tab once; later URL changes come from user tab clicks.
    if (!hasSetInitialTabFromUrl.current) {
      const requestedTab = searchParams.get('tab');

      if (requestedTab === 'nominations' && hasNominationAccess) {
        setActiveTab('nominations');
        hasSetInitialTabFromUrl.current = true;
      } else if (requestedTab === 'okrs' && hasOKRAccess) {
        setActiveTab('okrs');
        hasSetInitialTabFromUrl.current = true;
      } else if (noNomination === 'true') {
        setActiveTab('okrs');
        hasSetInitialTabFromUrl.current = true;
      }
    }
  }, [searchParams, hasNominationAccess, hasOKRAccess]);

  // Function to fetch basic employee info and okr_id
  const fetchBasicInfo = useCallback(async () => {
    if (authLoading || !user || !id) {
      return;
    }

    // Don't set loading here - it's managed by the calling function
    
    // Check if this is an OKR-only page (no nomination data)
    const noNomination = searchParams.get('no_nomination') === 'true';
    
    if (noNomination) {
      console.log('ℹ️ No nomination data - skipping nomination API call');
      // Create a minimal nomination object for display purposes
      // The employee info will be fetched when OKR data is loaded
      setNomination({
        id: id,
        employeeId: '',
        employeeName: 'Loading...',
        designation: '',
        department: '',
        managerName: '',
        employeeEmail: '',
        comments: '',
        nominationStatus: 'Pending',
        superiors: [],
        dotted_superiors: [],
        subordinates: [],
        additional_subordinates: [],
        peers: [],
        stakeholders: [],
        nominationApprover: '',
        okrs: []
      });
      return; // Skip the nomination API call
    }
    
    try {
      console.log('🔄 Fetching basic info for ID:', id);
      console.log('👤 Manager email:', user.email);
      
      // Use the specific API endpoint for nomination approval details
      const response = await nominationApprovalService.getNominationApprovalDetail(id, user.email);
      
      if (response.status && response.data) {
        console.log('✅ Found basic info:', response.data);
        
        const data: ApiResponseData = response.data;
        const employee = data.employee;
        
        // Store okr_id for later use
        console.log('🔍 API Response okr_id:', data.okr_id);
        if (data.okr_id) {
          console.log('✅ Setting okr_id to:', data.okr_id);
          setOkrId(data.okr_id);
        } else {
          console.log('⚠️ No okr_id found in API response');
        }
        
        // Map the API response to our interface (without nominations and OKRs initially)
        const nominationDetail: NominationApprovalDetail = {
          id: id,
          employeeId: employee.employee_id || '',
          employeeName: getEmployeeName(employee),
          designation: employee.designation || '',
          department: employee.department_name || '',
          managerName: employee.manager_name || '',
          employeeEmail: employee.email || '',
          comments: data.comments || '',
          nominationStatus: data.status?.toLowerCase() === 'pending' ? 'Pending' : 
                          data.status?.toLowerCase() === 'approved' ? 'Approved' : 
                          data.status?.toLowerCase() === 'rejected' ? 'Rejected' : 'Pending',
          // Initialize with empty arrays - will be loaded on demand
          superiors: [],
          dotted_superiors: [],
          subordinates: [],
          additional_subordinates: [],
          peers: [],
          stakeholders: [],
          nominationApprover: employee.manager_name,
          okrs: []
        };
        
        setNomination(nominationDetail);
      } else {
        console.error('❌ Invalid API response:', response);
        setNomination(null);
        setLoading(false); // Stop loading on error
      }
    } catch (error) {
      console.error('❌ Error fetching basic info:', error);
      
      // Check for 502 Bad Gateway error - navigate to full error page
      if (error && typeof error === 'object' && 'status_code' in error) {
        const statusCode = (error as { status_code: number }).status_code;
        if (statusCode === 502) {
          navigate('/error', {
            state: {
              errorType: 'bad-gateway',
              previousUrl: window.location.pathname,
              retryUrl: window.location.pathname
            }
          });
          return;
        }
      }
      
      setNomination(null);
      setLoading(false); // Stop loading on error
    }
  }, [authLoading, user, id, navigate, searchParams]);

  // Function to fetch OKR details
  const fetchOKRDetails = useCallback(async () => {
    // Check if this is a nominations-only page (no OKR data)
    const noOkr = !okrId || searchParams.get('no_okr') === 'true';
    
    if (noOkr) {
      console.log('ℹ️ No OKR data available - skipping OKR API call');
      setOkrLoading(false);
      return; // Don't make API call when okr_id is null
    }
    
    if (!user || !okrId) {
      console.log('ℹ️ No OKR ID available for this nomination');
      return;
    }

    // Prevent duplicate calls for the same OKR ID
    if (lastFetchedOkrIdRef.current === okrId) {
      console.log('ℹ️ OKR data already fetched for this ID');
      return;
    }

    setOkrLoading(true);
    lastFetchedOkrIdRef.current = okrId;
    
    try {
      console.log('🔄 Fetching OKR details for okr_id:', okrId);
      const okrResponse = await selfAppraisalService.getOKRDetails(okrId, user.email);
      
      console.log('📋 Full OKR API response:', okrResponse);
      
      // Check for OKRs in the correct path based on actual API response structure
      const okrsData = okrResponse.data?.okrs || okrResponse.data?.self_appraisal_list?.okrs;
      
      // Extract employee details from OKR API response
      const employeeData = okrResponse.data?.employee;
      console.log('👤 Employee data from OKR API:', employeeData);
      
      // Extract OKR status from API response
      const rawOkrStatus = okrResponse.data?.self_appraisal_list?.status;
      console.log('🎯 Raw OKR status from API:', rawOkrStatus);
      
      // Extract rejected remarks if status is rejected
      const rejectedRemarks = okrResponse.data?.self_appraisal_list?.rejected_remarks;
      console.log('📝 Rejected remarks from API:', rejectedRemarks);
      
      // Map API status to our status type
      let mappedOkrStatus: 'Approved' | 'Pending' | 'Rejected' | null = null;
      if (rawOkrStatus) {
        const statusLower = rawOkrStatus.toLowerCase();
        if (statusLower === 'approved') mappedOkrStatus = 'Approved';
        else if (statusLower === 'rejected') mappedOkrStatus = 'Rejected';
        else if (statusLower === 'pending') mappedOkrStatus = 'Pending';
        else if (statusLower === 'submitted') mappedOkrStatus = 'Pending'; // Treat submitted as pending
      }
      
      console.log('✅ Mapped OKR status:', mappedOkrStatus);
      setOkrStatus(mappedOkrStatus);
      
      // Store rejected remarks if available
      if (rejectedRemarks) {
        setOkrRejectedRemarks(rejectedRemarks);
      } else {
        setOkrRejectedRemarks(null);
      }
      
      if (okrResponse.status && okrsData && okrsData.length > 0) {
        console.log('✅ OKR details fetched successfully:', okrsData);
        
        // Map the OKR response to our OKRData interface
        const okrData: OKRData[] = okrsData.map((okr, index) => ({
          id: index + 1,
          objective: okr.objective || '',
          target: okr.target || '',
          endYearResult: okr.eyr || '',
          rating: okr.rating
        }));
        
        console.log('🎯 Mapped OKR data:', okrData);
        
        // Update nomination with OKR data and employee details if available
        setNomination(prev => {
          console.log('📝 Previous nomination state:', prev);
          
          // If we have employee data from OKR API and nomination was just a placeholder, update it
          const updated = prev ? {
            ...prev,
            okrs: okrData,
            // Update employee info if it was placeholder data (when no_nomination=true)
            ...(employeeData && prev.employeeName === 'Loading...' ? {
              employeeId: employeeData.employee_id || prev.employeeId,
              employeeName: getEmployeeName(employeeData) || prev.employeeName,
              designation: employeeData.designation || prev.designation,
              department: employeeData.department_name || prev.department,
              managerName: employeeData.manager_name || prev.managerName,
              employeeEmail: employeeData.email || prev.employeeEmail,
            } : {})
          } : null;
          
          console.log('📝 Updated nomination state:', updated);
          return updated;
        });
      } else {
        console.log('⚠️ No OKR data found in response');
        console.log('⚠️ Response structure:', okrResponse.data);
      }
    } catch (okrError) {
      console.error('❌ Error fetching OKR details:', okrError);
      showError('Error', 'Failed to load OKR details. Please try again.');
      lastFetchedOkrIdRef.current = null; // Reset on error to allow retry
    } finally {
      setOkrLoading(false);
    }
  }, [user, okrId, showError, searchParams]);

  // Function to fetch nomination details (superiors, subordinates, peers, stakeholders)
  const fetchNominationDetails = useCallback(async (forceRefresh = false) => {
    if (!user || !id) {
      return;
    }

    // Check if this is an OKR-only page (no nomination data)
    const noNomination = searchParams.get('no_nomination') === 'true';
    
    if (noNomination) {
      console.log('ℹ️ No nomination data available - skipping nomination details API call');
      setNominationLoading(false);
      return; // Don't make API call when nomination_id is null
    }

    // Don't fetch if already loaded, unless force refresh is requested
    if (!forceRefresh && nomination && (
      (nomination.superiors && nomination.superiors.length > 0) ||
      (nomination.dotted_superiors && nomination.dotted_superiors.length > 0) ||
      (nomination.subordinates && nomination.subordinates.length > 0) ||
      (nomination.additional_subordinates && nomination.additional_subordinates.length > 0) ||
      (nomination.peers && nomination.peers.length > 0) ||
      (nomination.stakeholders && nomination.stakeholders.length > 0)
    )) {
      console.log('ℹ️ Nomination details already loaded');
      return;
    }

    setNominationLoading(true);
    
    try {
      console.log('🔄 Fetching nomination details for ID:', id);
      
      const response = await nominationApprovalService.getNominationApprovalDetail(id, user.email);
      
      if (response.status && response.data) {
        console.log('✅ Nomination details fetched successfully');
        
        const data: ApiResponseData = response.data;
        const nominations = data.nominations;
        
        // Update nomination with nominations lists
        setNomination(prev => prev ? {
          ...prev,
          superiors: normalizeEmployees(nominations.superiors),
          dotted_superiors: normalizeEmployees(nominations.dotted_superiors),
          subordinates: normalizeEmployees(nominations.subordinates),
          additional_subordinates: normalizeEmployees(
            nominations.additional_subordinate || nominations.additional_subordinates,
          ),
          peers: normalizeEmployees(nominations.peers),
          stakeholders: normalizeEmployees(nominations.stakeholders),
          nominationApprover:
            getEmployeeName(nominations.nomination_manager?.[0] || {}) || prev.managerName
        } : null);
      } else {
        console.log('⚠️ No nomination details found in response');
      }
    } catch (nominationError) {
      console.error('❌ Error fetching nomination details:', nominationError);
      showError('Error', 'Failed to load nomination details. Please try again.');
    } finally {
      setNominationLoading(false);
    }
  }, [user, id, nomination, showError, searchParams]);

  // Initial data loading - load basic info and default tab data together
  useEffect(() => {
    const loadInitialData = async () => {
      if (authLoading || !user || !id || initialDataLoaded) {
        return;
      }

      setLoading(true);
      console.log('🔄 Loading initial data...');

      try {
        // Step 1: Fetch basic info first
        await fetchBasicInfo();
        
        // Note: fetchBasicInfo will set okrId, so we need to wait for it
        // The next useEffect will handle loading tab-specific data
      } catch (error) {
        console.error('❌ Error during initial load:', error);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id]);

  // Fetch data based on active tab - also handles initial load
  useEffect(() => {
    // Don't fetch if still loading basic info, auth is loading, or no user/id
    if (authLoading || !user || !id) {
      return;
    }

    // Don't fetch if we don't have nomination data yet
    if (!nomination) {
      return;
    }

    // If already loaded, don't run again
    if (initialDataLoaded) {
      return;
    }

    console.log('🔄 Initial data load useEffect triggered:', { 
      activeTab, 
      okrId, 
      userEmail: user?.email, 
      id,
      initialDataLoaded,
      hasOkrId: !!okrId,
      willFetchOKRs: activeTab === 'okrs' && okrId && user?.email,
      willFetchNominations: activeTab === 'nominations' && user && id
    });
    
    const loadTabData = async () => {
      // For initial load, keep main loading screen on
      setLoading(true);
      
      if (activeTab === 'okrs') {
        if (okrId && user?.email) {
          console.log('✅ Fetching OKR details for initial load');
          
          // Reset tracking to fetch fresh data
          lastFetchedOkrIdRef.current = null;
          await fetchOKRDetails();
        } else {
          console.log('⚠️ No OKR ID available - skipping OKR fetch');
        }
        
        // Mark initial data as loaded regardless of okr_id presence
        setInitialDataLoaded(true);
        setLoading(false);
      } else if (activeTab === 'nominations') {
        console.log('✅ Fetching nomination details for initial load');
        
        await fetchNominationDetails();
        
        // Mark initial data as loaded
        setInitialDataLoaded(true);
        setLoading(false);
      }
    };

    loadTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomination, okrId]);

  // Separate effect for handling tab switches after initial load
  useEffect(() => {
    // Only handle tab switches after initial data is loaded
    if (!initialDataLoaded || !user || !id) {
      return;
    }

    console.log('🔄 Tab switch detected:', { activeTab });

    const loadTabDataOnSwitch = async () => {
      if (activeTab === 'okrs') {
        if (okrId && user?.email) {
          console.log('✅ Fetching OKR details on tab switch');
          // Reset tracking to fetch fresh data
          lastFetchedOkrIdRef.current = null;
          await fetchOKRDetails();
        } else {
          console.log('⚠️ No OKR ID available for this employee');
        }
      } else if (activeTab === 'nominations') {
        console.log('✅ Fetching nomination details on tab switch');
        await fetchNominationDetails();
      }
    };

    loadTabDataOnSwitch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleApprove = () => {
    setShowApproveDialog(true);
  };

  const handleConfirmApprove = async () => {
    if (!user?.email || !id) return;
    
    setActionLoading(true);
    try {
      // Check which tab is active and call appropriate API
      if (activeTab === 'okrs') {
        // Call OKR approve API
        if (!okrId) {
          showError('Error', 'OKR ID is missing. Please try again.');
          return;
        }
        
        console.log('🔄 Approving OKR self-appraisal:', okrId);
        
        const response = await selfAppraisalService.submitOKRAction(
          okrId,
          user.email,
          'approved',
          'approved'
        );
        
        if (response.status) {
          console.log('✅ OKR self-appraisal approved successfully');
          setOkrStatus('Approved');
          setShowApproveDialog(false);
          setRedirectDestination('team-review');
          setRedirectCountdown(30);
          setShowRedirectDialog(true);
        } else {
          console.error('❌ Failed to approve OKR:', response.error);
          showError('Failed to Approve', response.error || 'Failed to approve OKR self-appraisal. Please try again.');
        }
      } else {
        // Call Nomination approve API
        console.log('🔄 Approving nomination:', id);
        
        const response = await nominationApprovalService.updateNominationAction(
          id,
          user.email,
          'approved',
          'Approved' // Default comment for approval
        );
        
        if (response.status) {
          console.log('✅ Nomination approved successfully');
          setShowApproveDialog(false);
          setRedirectDestination('360-feedback');
          setRedirectCountdown(30);
          setShowRedirectDialog(true);
        } else {
          console.error('❌ Failed to approve nomination:', response.error);
          const errorMessage = getNominationErrorMessage(response);
          showError('Failed to Approve', errorMessage || 'Failed to approve nomination. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Error during approval:', error);
      const errorMessage = getNominationErrorMessage(error);
      showError('Error', errorMessage || `Error approving ${activeTab === 'okrs' ? 'OKR self-appraisal' : 'nomination'}. Please try again.`);
    } finally {
      setActionLoading(false);
      setShowApproveDialog(false);
    }
  };

  const handleCancelApprove = () => {
    setShowApproveDialog(false);
  };

  const handleCancelRedirect = () => {
    setShowRedirectDialog(false);
  };

  const handleRedirectNow = () => {
    redirectAfterApproval();
  };

  const handleReject = () => {
    setShowRejectDialog(true);
  };

  const handleConfirmReject = async () => {
    if (!user?.email || !id || !rejectReason.trim()) {
      showError('Validation Error', 'Please provide a reason for rejection.');
      return;
    }
    
    setActionLoading(true);
    try {
      // Check which tab is active and call appropriate API
      if (activeTab === 'okrs') {
        // Call OKR reject API
        if (!okrId) {
          showError('Error', 'OKR ID is missing. Please try again.');
          return;
        }
        
        console.log('🔄 Rejecting OKR self-appraisal:', okrId, 'Reason:', rejectReason);
        
        const response = await selfAppraisalService.submitOKRAction(
          okrId,
          user.email,
          'rejected',
          rejectReason.trim()
        );
        
        if (response.status) {
          console.log('✅ OKR self-appraisal rejected successfully');
          setShowRejectDialog(false);
          setRejectReason('');
          window.location.reload();
        } else {
          console.error('❌ Failed to reject OKR:', response.error);
          showError('Failed to Reject', response.error || 'Failed to reject OKR self-appraisal. Please try again.');
        }
      } else {
        // Call Nomination reject API
        console.log('🔄 Rejecting nomination:', id, 'Reason:', rejectReason);
        
        const response = await nominationApprovalService.updateNominationAction(
          id,
          user.email,
          'rejected',
          rejectReason.trim()
        );
        
        if (response.status) {
          console.log('✅ Nomination rejected successfully');
          setShowRejectDialog(false);
          setRejectReason('');
          window.location.reload();
        } else {
          console.error('❌ Failed to reject nomination:', response.error);
          const errorMessage = getNominationErrorMessage(response);
          showError('Failed to Reject', errorMessage || 'Failed to reject nomination. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Error during rejection:', error);
      const errorMessage = getNominationErrorMessage(error);
      showError('Error', errorMessage || `Error rejecting ${activeTab === 'okrs' ? 'OKR self-appraisal' : 'nomination'}. Please try again.`);
    } finally {
      setActionLoading(false);
      setShowRejectDialog(false);
      setRejectReason('');
    }
  };

  const handleCancelReject = () => {
    setShowRejectDialog(false);
    setRejectReason('');
  };

  const handleBack = () => {
    navigate('/approvals');
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading nomination details...</p>
        </div>
      </div>
    );
  }

  // Only show error state if loading is complete and nomination is still null
  if (!loading && !nomination) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <NoDataState
          title="Nomination Not Found"
          description="The requested nomination could not be found. It may have been removed or you may not have permission to view it."
        />
      </div>
    );
  }

  // Guard clause: if nomination is still null but loading is false, return null to prevent render
  if (!nomination) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <div className="flex items-center space-x-2 cursor-pointer" onClick={handleBack}>
        <img src={arrowBackIcon} alt="Back" className="w-6 h-6" />
        <span className="text-purple-600 font-medium">Back to Employee List</span>
      </div>

      {/* Tabs */}
      <div className="responsive-tabs flex gap-2 mx-1">
        <DisabledTabTooltip
          disabled={!hasOKRAccess}
          content="This feature is currently not available for this cycle."
        >
          <button
            onClick={() => { if (hasOKRAccess) { setActiveTab('okrs'); setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'okrs'); return next; }); } }}
            disabled={!hasOKRAccess}
            className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              !hasOKRAccess
                ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                : activeTab === 'okrs' 
                  ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800" 
                  : "text-gray-600 hover:bg-gray-200 cursor-pointer"
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Self Ratings & OKRs
          </button>
        </DisabledTabTooltip>
        <DisabledTabTooltip
          disabled={!hasNominationAccess}
          content={isStartOrMidCycle
            ? 'Nominations are not available for Target setting and Mid cycle'
            : 'This feature is currently available only for India employees, and will soon open for the rest'}
        >
          <button
            onClick={() => { if (hasNominationAccess) { setActiveTab('nominations'); setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'nominations'); return next; }); } }}
            disabled={!hasNominationAccess}
            className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              !hasNominationAccess
                ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                : activeTab === 'nominations' 
                  ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800" 
                  : "text-gray-600 hover:bg-gray-200 cursor-pointer"
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Nominations
          </button>
        </DisabledTabTooltip>
      </div>

      {/* Employee Information */}
      <Card>
        <div className="">
          <div>
            <div className="text-primary-800 text-base font-regular font-verdana mb-4 border bg-custom-light-purple p-1 pl-2">
              Employee Information
            </div>
            {/* Employee Details */}
            <div className="grid grid-cols-1 gap-2 px-2 lg:grid-cols-2 lg:gap-8">
              <div className="space-y-3">
                <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Employee ID </span>
                  <span className="break-words text-gray-900 text-sm">{nomination.employeeId || '-'}</span>
                </div>
                <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Designation</span>
                  <span className="break-words text-gray-900 text-sm">{nomination.designation || '-'}</span>
                </div>
                <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Department</span>
                  <span className="break-words text-gray-900 text-sm">{nomination.department || '-'}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Name of Employee</span>
                  <NameWithEmailTooltip
                    as="span"
                    className="text-gray-900 text-sm"
                    name={nomination.employeeName}
                    email={nomination.employeeEmail}
                  />
                </div>
                <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Manager Name</span>
                  <NameWithEmailTooltip
                    as="span"
                    className="text-gray-900 text-sm"
                    name={nomination.managerName}
                  />
                </div>
                 <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                  <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Employee e-mail ID</span>
                  <span className="break-all text-gray-900 text-sm">{nomination.employeeEmail || '-'}</span>
                </div>
              
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'okrs' ? (
        /* OKRs Tab Content */
        <div className="space-y-6">
          {/* OKR Table */}
          <div className="table-container">
            {/* Table Header */}
            <div className="bg-primary-800 rounded-lg px-6 py-4 border-b border-gray-200 mb-3">
              <div className="grid grid-cols-12 gap-4 text-left text-base font-medium text-white tracking-wider">
                <div className="text-base col-span-1">SNO.</div>
                <div className="text-base col-span-3">Objective</div>
                <div className="text-base col-span-2">Target</div>
                <div className="text-base col-span-3">{resultColumnLabel}</div>
                <div className="text-base col-span-3">Ratings</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="space-y-3 relative">
              {(() => {
                console.log('🔍 Table Render Debug:', {
                  okrLoading,
                  nomination,
                  hasOkrs: nomination?.okrs,
                  okrsLength: nomination?.okrs?.length,
                  okrsData: nomination?.okrs
                });
                return null;
              })()}
              {okrLoading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" />
                  <span className="ml-3 text-gray-600">Loading OKR details...</span>
                </div>
              ) : searchParams.get('no_okr') === 'true' || !okrId ? (
                <NoDataState
                  title="No OKRs Found"
                  description="This employee does not have any OKR (Objectives and Key Results) data available."
                />
              ) : nomination?.okrs && nomination.okrs.length > 0 ? (
                nomination.okrs.map((okr, index) => (
                  <div 
                    key={okr.id}
                    className="border border-gray-200 rounded-lg px-6 py-4"
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-1">
                        <div className="flex items-center justify-center" style={{ minHeight: "48px" }}>
                          <span className="font-medium text-gray-700 text-base">{index + 1}.</span>
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="text-base text-gray-600" style={{ whiteSpace: "pre-wrap" }}>{okr.objective || 'N/A'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-base text-gray-600" style={{ whiteSpace: "pre-wrap" }}>{okr.target || 'N/A'}</div>
                      </div>
                      <div className="col-span-3">
                        <div className="text-base text-gray-600" style={{ whiteSpace: "pre-wrap" }}>{okr.endYearResult || 'N/A'}</div>
                      </div>
                      <div className="col-span-3">
                        <div className="text-base text-gray-600">
                          {okr.rating ? (
                            okr.rating === 1 ? '1-Excel' :
                            okr.rating === 2 ? '2-Fully Met' :
                            okr.rating === 3 ? '3-Mostly Met' :
                            okr.rating === 4 ? '4-Requires Action' : okr.rating
                          ) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No OKRs Found</h3>
                  <p className="text-gray-500">No objectives and key results have been set.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Nominations Tab Content */
        <div className="space-y-6">
          {/* Check if this is a no-nomination case */}
          {searchParams.get('no_nomination') === 'true' ? (
            <NoDataState
              title="No Nominations Found"
              description="This employee does not have any nomination data available."
            />
          ) : nominationLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">Loading nomination details...</span>
            </div>
          ) : (
            <>
              <div className="text-primary-800 text-lg font-semibold font-verdana mb-6">
                Nominated Colleagues
              </div>
        
        {/* Superiors */}
        <div className="bg-white border border-purple-200 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <label className="block text-base font-normal text-gray-700">Superiors    
              {/* <span className="text-base text-gray-400 italic ml-2">(Immediate superiors that you have been working with for the current year - Auto-populated from system)</span> */}
        </label>
          </div>
          {nomination.superiors && nomination.superiors.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.superiors.map((superior, index) => (
                <div
                  key={superior.employee_id || `${superior.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={superior.full_name}
                    email={superior.email}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 italic p-3 bg-gray-50 rounded-xl text-center">
              No superiors nominated
            </div>
          )}
        </div>

        {/* Additional Superiors */}
        {nomination.dotted_superiors && nomination.dotted_superiors.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <label className="block text-base font-normal text-gray-700">Additional Superiors</label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.dotted_superiors.map((superior, index) => (
                <div
                  key={superior.employee_id || `${superior.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={superior.full_name}
                    email={superior.email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subordinates */}
        {nomination.subordinates && nomination.subordinates.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <label className="block text-base font-normal text-gray-700">Subordinates

                {/* <span className="text-base text-gray-400 italic ml-2">(1-10 of your direct report subordinates that you have been working with for more than three (3) months in the current year)</span> */}
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.subordinates.map((subordinate, index) => (
                <div
                  key={subordinate.employee_id || `${subordinate.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={subordinate.full_name}
                    email={subordinate.email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Subordinates */}
        {nomination.additional_subordinates && nomination.additional_subordinates.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <label className="block text-base font-normal text-gray-700">Additional Subordinates</label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.additional_subordinates.map((subordinate, index) => (
                <div
                  key={subordinate.employee_id || `${subordinate.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={subordinate.full_name}
                    email={subordinate.email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {nomination.peers && nomination.peers.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <label className="block text-base font-normal text-gray-700">Peers</label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.peers.map((peer, index) => (
                <div
                  key={peer.employee_id || `${peer.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={peer.full_name}
                    email={peer.email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stakeholders */}
        {nomination.stakeholders && nomination.stakeholders.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <label className="block text-base font-normal text-gray-700">Stakeholders</label>
              {/* <span className="text-base text-gray-400 italic ml-2">(This is optional. Please restrict to a maximum of three (3) stakeholders)</span> */}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {nomination.stakeholders.map((stakeholder, index) => (
                <div
                  key={stakeholder.employee_id || `${stakeholder.full_name}-${index}`}
                  className="flex items-center p-2 bg-custom-light-purple rounded-lg"
                >
                  <NameWithEmailTooltip
                    as="span"
                    className="text-purple-800 text-base font-medium"
                    name={stakeholder.full_name}
                    email={stakeholder.email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
        </div>
      )}

      {/* Status Display with enhanced design - Tab-specific messages */}
      {activeTab === 'okrs' ? (
        /* OKR Status Messages */
        <>
          {okrStatus === 'Approved' && (
            <div className="bg-green-50 border border-green-200 rounded-xl py-4 px-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-green-800">OKR Self-Appraisal Approved</span>
              </div>
              <p className="text-green-700 text-sm mt-1">The OKR self-appraisal has been reviewed and approved.</p>
            </div>
          )}

          {okrStatus === 'Rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg py-4 px-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="font-medium text-red-800">OKR Self-Appraisal Rejected</span>
              </div>
              {okrRejectedRemarks && (
                <p className="text-red-700 text-sm mt-1">Reason: <span className="font-semibold">{okrRejectedRemarks}</span></p>
              )}
            </div>
          )}

          {okrStatus === 'Pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg py-4 px-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-yellow-800">OKR Self-Appraisal Pending Review</span>
              </div>
              <p className="text-yellow-700 text-sm mt-1">The OKR self-appraisal is awaiting review.</p>
            </div>
          )}
        </>
      ) : (
        /* Nomination Status Messages */
        <>
          {nomination.nominationStatus === 'Approved' && (
            <div className="bg-green-50 border border-green-200 rounded-xl py-4 px-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-green-800">Nominations Approved</span>
              </div>
              <p className="text-green-700 text-sm mt-1">These nominations have been approved and are final.</p>
            </div>
          )}

          {nomination.nominationStatus === 'Rejected' && nomination.comments && (
            <div className="bg-red-50 border border-red-200 rounded-lg py-4 px-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="font-medium text-red-800">Nominations Rejected</span>
              </div>
              <p className="text-red-700 text-sm mt-1">Reason: <span className="font-semibold">{nomination.comments || "No reason provided"}</span></p>
            </div>
          )}
        </>
      )}

      {/* Action Buttons or Status Display */}
      {((activeTab === 'okrs' && okrStatus === 'Pending') || 
        (activeTab === 'nominations' && nomination.nominationStatus === 'Pending' && searchParams.get('no_nomination') !== 'true')) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button
            onClick={handleReject}
            disabled={actionLoading}
            variant="outline"
            className="px-6 py-2 !bg-white !text-red-600 !border-red-600 rounded-lg hover:!bg-red-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? 'Processing...' : 'Reject'}
          </Button>
          <Button
            onClick={handleApprove}
            disabled={actionLoading}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? 'Processing...' : 'Approve'}
          </Button>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog
        isOpen={showRejectDialog}
        onClose={handleCancelReject}
        title={activeTab === 'okrs' 
          ? `Reject OKR Self-Appraisal (${nomination?.employeeName})` 
          : `Reject Selected Nominations (${nomination?.employeeName})`}
        type="warning"
        size="extended"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Submit"
        onConfirm={handleConfirmReject}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              {activeTab === 'okrs'
                ? `Please let the employee know why you are rejecting their OKR self-appraisal. You can provide feedback on specific objectives or ratings.`
                : `Please let the employee know why you are rejecting their selected nominations. You can also suggest better options for nomination.`}
            </p>
            <div>
              <label htmlFor="rejectReason" className="block text-base font-normal text-gray-700 mb-2">
                Enter your reason for rejection
              </label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none ${
                  rejectReason.trim().length === 0 ? 'border-red-300' : 'border-gray-300'
                }`}
                rows={4}
                placeholder="Enter your reason for rejection (required)"
                required
              />
              <div className="h-5 mt-1">
                {rejectReason.trim().length === 0 && (
                  <p className="text-red-500 text-xs">Reason for rejection is required</p>
                )}
              </div>
            </div>
          </div>
        }
      />

      {/* Approve Confirmation Dialog */}
      <Dialog
        isOpen={showApproveDialog}
        onClose={handleCancelApprove}
        title={activeTab === 'okrs'
          ? `Approve OKR Self-Appraisal (${nomination?.employeeName})`
          : `Approve Selected Nominations (${nomination?.employeeName})`}
        type="info"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Approve"
        onConfirm={handleConfirmApprove}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              {activeTab === 'okrs'
                ? <>Are you sure you want to approve the OKR self-appraisal for <strong>{nomination?.employeeName}</strong>?</>
                : <>Are you sure you want to approve the selected nominations for <strong>{nomination?.employeeName}</strong>?</>}
            </p>
            <p className="text-gray-600 text-sm">
              {activeTab === 'okrs'
                ? `Once approved, the OKR self-appraisal will be finalized and locked.`
                : `Once approved, these nominations will be final and the feedback collection process will begin.`}
            </p>
          </div>
        }
      />

      {/* Post-approval redirect dialog */}
      <Dialog
        isOpen={showRedirectDialog}
        onClose={handleCancelRedirect}
        title={redirectDestination === '360-feedback' ? 'Nominations Approved' : 'OKR Self-Appraisal Approved'}
        type="success"
        message={
          <div className="space-y-3">
            <p>
              {redirectDestination === '360-feedback'
                ? 'The nominations were approved successfully. You will be redirected to 360 Feedback in '
                : 'The OKR self-appraisal was approved successfully. You will be redirected to Team Review in '}
              <strong>{redirectCountdown} seconds</strong>.
            </p>
          </div>
        }
        customActions={
          <>
            <button
              type="button"
              onClick={handleCancelRedirect}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRedirectNow}
              className="rounded-lg bg-purple-800 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors"
            >
              {redirectDestination === '360-feedback' ? 'Go to 360 Feedback' : 'Go to Team Review'}
            </button>
          </>
        }
      />
      
      {/* Toast Notification Dialog */}
      <Dialog
        isOpen={toastState.isOpen}
        onClose={closeToast}
        title={toastState.title}
        message={toastState.message}
        type={toastState.type}
        showCancel={toastState.showCancel}
        confirmText={toastState.confirmText}
        cancelText={toastState.cancelText}
        onConfirm={toastState.onConfirm || closeToast}
      />
    </div>
  );
};

export default NominationApprovalDetailPage;
